import { clamp } from "lodash-es";
import { readonly, ref } from "vue";

import type { CardCreateProps } from "@/action/addCard";
import { cardActions } from "@/action/cardActions";
import { internalActions } from "@/action/internalActions";
import type { ActionContext } from "@/action/types";
import type { Drag } from "@/components/gestures/drag";
import { registerDrag } from "@/components/gestures/drag";
import { relativeFromViewport } from "@/math/coordinate-utils";
import type { ActionSource } from "@/model/action";
import type { Card } from "@/model/card";
import type { ViewportCoordinate } from "@/model/coordinates";
import type { StickyType } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { isInsideParent } from "@/utils/dom/coordinates";

export type DragStickyOptions = BaseOptions & ModeOptions;

interface BaseOptions {
  // Tracking label for the card action source (e.g., "board-actions-menu-drag", "backlog-selector-mirror")
  trackingLabel: ActionSource | ActionContext;
  // Optional CSS selector for parent container - card creation/mirroring is delayed until pointer leaves this element
  parentSelector?: string;
  // Optional callback triggered when drag starts (before card creation/mirroring)
  onDragStart?: () => void;
  // Whether to auto-focus and enlarge the sticky after drop (defaults to true)
  autoFocus?: boolean;
}

type ModeOptions =
  | { mode: "create"; getCardProps: (dragStart: ViewportCoordinate) => CardCreateProps }
  | { mode: "mirror"; cardProps: { id: Card["id"]; type: StickyType } };

/**
 * Composable for drag-to-create or drag-to-mirror sticky note functionality.
 *
 * Supports two modes:
 * - **Create mode**: Creates a brand new sticky note (e.g., from ALM issues)
 * - **Mirror mode**: Mirrors an existing sticky to another board (e.g., from backlog)
 *
 * This composable handles the entire flow:
 * 1. Registers drag gesture handlers
 * 2. Delays card creation/mirroring until pointer leaves parent container
 * 3. Seamlessly transitions to normal card dragging
 * 4. Tracks drag state for visual feedback
 *
 * @example Create mode
 * ```ts
 * const { handlePointerDown, isDragging } = useDragToCreateSticky({
 *   mode: "create",
 *   getCardProps: (dragStart) => ({
 *     pos: minus(dragStart),
 *     type: stickyType.value,
 *     text: issue.summary
 *   }),
 *   trackingLabel: "issue-selector-drag",
 *   parentSelector: ".issue-selector-panel",
 *   fromPalette: false
 * });
 * ```
 *
 * @example Mirror mode
 * ```ts
 * const { handlePointerDown, isDragging } = useDragToCreateSticky({
 *   mode: "mirror",
 *   cardProps: {
 *     id: existingStickyId,
 *     type: stickyType.value // for tracking
 *   },
 *   trackingLabel: "backlog-selector-mirror",
 *   parentSelector: ".issue-selector-panel"
 * });
 * ```
 */
export function useDragToCreateSticky(options: DragStickyOptions) {
  const { trackingLabel, onDragStart, parentSelector, autoFocus = true } = options;
  const isDragging = ref(false);

  function handlePointerDown(e: PointerEvent) {
    let created = false;
    const parentContainer = parentSelector && document.querySelector(parentSelector);

    registerDrag(e, {
      start() {
        isDragging.value = true;

        // Call onDragStart if we're not waiting for the element to leave the parent container
        if (!parentContainer) {
          onDragStart?.();
        }
      },
      /**
       * If the card hasn't yet been created, calculates the pointer position
       * relative to the given parent container. When the pointer leaves the
       * parent container, creates the sticky
       */
      move(drag) {
        if (created) return drag.pos;

        const placeCardOnBoard = () => {
          created = true;
          const currentPos = drag.originalPos();

          if (options.mode === "create") {
            // CREATE MODE: Pass ViewportCoordinate to addCard, which converts it to RelativeCoordinate
            const cardProps = options.getCardProps(currentPos);
            void cardActions
              .add(trackingLabel, { ...cardProps, pos: cardProps.pos ?? "auto" }, false)
              .then((id) => {
                if (id) {
                  updateCardPositionAndStartDrag(id, drag);
                }
              });
          } else {
            // MIRROR MODE: Convert ViewportCoordinate to RelativeCoordinate for mirrorWhileDragging
            const id = options.cardProps.id;
            const type = options.cardProps.type;

            const targetBoard = useBoardStore().currentBoard();
            const trackingData = { functionality: type.functionality, fromBoard: type.origin };

            // mirrorWhileDragging expects RelativeCoordinate
            const relativePos = relativeFromViewport(currentPos);

            void cardActions
              .mirrorWhileDragging(trackingLabel, id, targetBoard, trackingData, relativePos)
              .then((mirroredId?: string) => {
                if (!mirroredId) return;
                updateCardPositionAndStartDrag(mirroredId, drag);
              });
          }
        };

        // If we can't find the parent container, just put the card on the board right away
        if (!parentContainer) {
          placeCardOnBoard();
          return drag.pos;
        }

        // Check if pointer has left the parent container
        const pos = drag.originalPos();
        const isInsideContainer = isInsideParent(pos, parentContainer as HTMLElement);

        // Create card when leaving the container
        if (!isInsideContainer) {
          // If the caller specified a parent container, call onDragStart now too
          onDragStart?.();
          placeCardOnBoard();
        }

        return drag.pos;
      },
      stop() {
        isDragging.value = false;
      },
    });
  }

  /**
   * Updates the card position to the current pointer location and starts dragging.
   * This prevents visible jumps during fast drags where the pointer has moved
   * significantly between card creation and render.
   */
  function updateCardPositionAndStartDrag(cardId: string, drag: Drag) {
    const board = useBoardStore().currentBoard();
    const card = board.cards[cardId];
    if (card) {
      const currentPos = relativeFromViewport(drag.originalPos());
      // Clamp coordinates to prevent negative values - needed for correct link calculation,
      // otherwise some links may lead outside of the board
      card.meta.pos.x = clamp(currentPos.x, 0, 1);
      card.meta.pos.y = clamp(currentPos.y, 0, 1);

      // Call stopAlter to trigger backend iteration recalculation
      // Note for the issue selector: stop() callback is not called because pointerup happens on card element, not original element
      // For fast drags, cardDragHandler will call stopAlter again with final position when drag ends
      internalActions.stopAlter(cardId, card.meta.pos);
    }

    // Only start card drag if the drag is still active
    if (isDragging.value) {
      startCardDrag(cardId, drag);
    }
  }

  /**
   * Initiates dragging of a newly created/mirrored card by storing metadata
   * in the dragging store and dispatching a pointerdown event to trigger cardDragHandler.
   */
  function startCardDrag(id: string, drag: Drag) {
    const elem = document.getElementById(id);
    if (!elem) return;

    const initialPos = drag.originalPos();

    // Store metadata in dragging store for cardDragHandler to use when the drag actually starts
    useDraggingStore().setExternalDragMetadata(id, {
      grabFromOutside: true,
      autoFocus,
      initialPos,
    });

    // Dispatch pointerdown to trigger cardDragHandler
    elem.dispatchEvent(
      new PointerEvent("pointerdown", {
        pointerId: drag.pointerId,
        buttons: 1,
        clientX: initialPos.x,
        clientY: initialPos.y,
      }),
    );

    // Don't dispatch synthetic pointerup - let the user's actual mouse release end the drag
    // The original immediate pointerup was preventing Firefox from continuing the drag gesture
  }

  return {
    handlePointerDown,
    isDragging: readonly(isDragging),
  };
}
